import type { ApiInputToken, MarketForRewards, MarketRewards } from '@morpho-org/dapp-core'

import type { MarketSdkState } from '@/lib/procedures/marketActions.contract'

import { converter } from '@/selectors/converter'

/** The part of `market.sdkState` the panel renders; the server sends the client only this. */
export type ActionPanelState = Pick<
  MarketSdkState,
  'market' | 'supply' | 'borrow' | 'collateralVault'
>

type ActionToken = MarketSdkState['market']['loanToken']
type ApyReward = MarketSdkState['supply']['rewards'][number]

const toApiToken = (token: ActionToken, chainId: number): ApiInputToken => ({
  address: token.address,
  name: token.name ?? undefined,
  symbol: token.symbol ?? undefined,
  decimals: token.decimals,
  logoURI: token.logoUri,
  priceUsd: token.priceUsd,
  chain: { id: chainId }
})

const toRewardToken = ({ token }: ApyReward): ApiInputToken => ({
  address: token.address,
  symbol: token.symbol ?? undefined,
  logoURI: token.logoUri,
  // Reward rows render the symbol, logo and APR only; the procedure sends no decimals.
  decimals: 18,
  chain: { id: token.chainId }
})

/**
 * One entry per reward token with both sides' APRs, as the rewards helpers read them. The
 * procedure already leaves out Vault V2 only campaigns, which a direct market position does not earn.
 */
function toMarketRewards({ supply, borrow }: ActionPanelState): MarketRewards[] {
  const byToken = new Map<string, MarketRewards>()
  const add = (side: 'supplyApr' | 'borrowApr', rewards: readonly ApyReward[]) => {
    for (const reward of rewards) {
      const key = `${reward.token.chainId}:${reward.token.address.toLowerCase()}`
      const entry = byToken.get(key) ?? { asset: toRewardToken(reward) }
      entry[side] = (entry[side] ?? 0) + reward.apr
      byToken.set(key, entry)
    }
  }
  add('supplyApr', supply.rewards)
  add('borrowApr', borrow.rewards)
  return [...byToken.values()]
}

/** The panel's tokens, Morpho SDK market params and rewards, from the procedure's state. */
export function toActionPanelMarket(state: ActionPanelState, chainId: number) {
  const { market, collateralVault } = state
  const loanAsset = toApiToken(market.loanToken, chainId)
  const collateralAsset = market.collateralToken && {
    ...toApiToken(market.collateralToken, chainId),
    vault: collateralVault && {
      name: collateralVault.name,
      address: collateralVault.address,
      asset: { address: collateralVault.assetAddress }
    }
  }
  const lltv = BigInt(market.lltv)
  const marketForRewards: MarketForRewards = {
    loanAsset,
    state: { rewards: toMarketRewards(state) }
  }
  return {
    loanAsset,
    collateralAsset,
    lltv,
    marketParams: converter.getMarketParams({
      collateralAsset,
      loanAsset,
      oracle: { address: market.oracleAddress },
      irmAddress: market.irmAddress,
      lltv: market.lltv
    }),
    marketForRewards
  }
}
